import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MagnifyingGlass, MicrophoneStage, CaretDown, CaretUp, ArrowsClockwise,
  Star, Play, MapPin, ChatCircleDots, Bell, Image as ImageIcon, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { useAdminMode } from "../../context/AdminModeContext";

function StatPill({ icon: Icon, label, value, accent = "text-neon-cyan" }) {
  return (
    <div className="rounded-md border border-ink-600 bg-ink-900/60 px-3 py-2">
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 inline-flex items-center gap-1 font-mono">
        <Icon size={11} weight="bold" /> {label}
      </div>
      <div className={`mt-0.5 text-base font-bold font-mono ${accent}`}>{value}</div>
    </div>
  );
}

export default function ArtistDirectory() {
  const { isFull, isPresident } = useAdminMode();
  const depth = isPresident ? "full" : isFull ? "detail" : "basic";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openName, setOpenName] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/artists${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`);
      setItems(data.artists || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (name) => {
    if (openName === name) {
      setOpenName(null);
      setDetail(null);
      return;
    }
    setOpenName(name);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/artists/detail?name=${encodeURIComponent(name)}&depth=${depth}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <section data-testid="artist-directory">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2 inline-flex items-center gap-2">
            <MicrophoneStage size={14} weight="fill" /> Artist Directory
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Every byte of data, by artist name.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Search any artist on the roster — totals follow the artist across slots. Detail depth scales with your access level.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search artist name…"
              className="pl-8 pr-3 py-1.5 rounded-md bg-ink-900 border border-ink-600 text-white text-xs w-52 focus:outline-none focus:border-neon-cyan/60"
              data-testid="artist-search"
            />
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="artist-reload">
            <ArrowsClockwise size={14} /> Reload
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-zinc-500 text-sm" data-testid="artist-empty">
            {query.trim() ? "No artist matches that search." : "No artists with data yet."}
          </div>
        ) : (
          <ul>
            {filtered.map((a) => {
              const open = openName === a.artist_name;
              return (
                <li key={a.artist_name} className="border-b border-ink-700/60 last:border-0" data-testid={`artist-row-${a.artist_name}`}>
                  <button
                    onClick={() => toggle(a.artist_name)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-ink-800/40 transition-colors"
                    data-testid={`artist-toggle-${a.artist_name}`}
                  >
                    <MicrophoneStage size={16} className="text-neon-cyan shrink-0" weight="bold" />
                    <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
                      <span className="col-span-12 sm:col-span-4 text-white text-sm font-semibold truncate">{a.artist_name}</span>
                      <span className="col-span-3 sm:col-span-2 text-xs font-mono text-zinc-400">{a.total_songs} song{a.total_songs === 1 ? "" : "s"}</span>
                      <span className="col-span-3 sm:col-span-2 text-xs font-mono"><span className="text-neon-cyan">{a.total_plays}</span> plays</span>
                      <span className="col-span-3 sm:col-span-2 text-xs font-mono"><span className="text-amber-300">{a.total_votes}</span> votes</span>
                      {a.rating_avg > 0 && (
                        <span className="col-span-3 sm:col-span-2 text-xs font-mono text-zinc-300 inline-flex items-center gap-1">
                          <Star size={11} weight="fill" className="text-amber-300" />{a.rating_avg.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {open ? <CaretUp size={14} className="text-zinc-500" /> : <CaretDown size={14} className="text-zinc-500" />}
                  </button>

                  {open && (
                    <div className="px-4 pb-5 pt-1" data-testid={`artist-detail-${a.artist_name}`}>
                      {detailLoading || !detail ? (
                        <p className="text-zinc-500 text-sm py-4">Loading detail…</p>
                      ) : (
                        <ArtistDetailBody data={detail} depth={depth} />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-3 text-xs text-zinc-500 font-mono" data-testid="artist-tier-hint">
        Detail depth: <span className="text-neon-cyan">{depth === "basic" ? "Agent (totals only)" : depth === "detail" ? "Full (per-song + cities)" : "Owner (cities + ZIP-level)"}</span>
      </p>
    </section>
  );
}

function ArtistDetailBody({ data, depth }) {
  const t = data.totals || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatPill icon={Play} label="Plays" value={t.plays} />
        <StatPill icon={Star} label="Votes" value={t.votes} accent="text-amber-300" />
        <StatPill icon={ChatCircleDots} label="Comments" value={t.comments} accent="text-neon-magenta" />
        <StatPill icon={Bell} label="Subs" value={t.subscribers} accent="text-zinc-300" />
      </div>

      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Active slots</p>
        <ul className="space-y-2" data-testid="artist-songs">
          {data.songs.map((s) => (
            <li key={s.rank} className="rounded-md border border-ink-700 bg-ink-800/40 p-3 flex items-start gap-3" data-testid={`artist-song-${s.rank}`}>
              {s.image_path ? (
                <img src={fileUrl(s.image_path)} alt="" className="w-12 h-12 rounded object-cover border border-ink-600 shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded border border-ink-600 flex items-center justify-center text-zinc-600 shrink-0"><ImageIcon size={16} /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs text-neon-cyan">#{String(s.rank).padStart(2, "0")}</span>
                  <span className="text-sm text-white font-semibold truncate">{s.song_title || "—"}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs font-mono text-zinc-400">
                  <span><span className="text-neon-cyan">{s.plays}</span> plays</span>
                  <span><span className="text-amber-300">{s.votes}</span> votes ({s.rating_avg.toFixed(2)}★)</span>
                  <span>{s.comments} comments</span>
                  <span>{s.clicks} clicks</span>
                  <span>{s.subscribers} subs</span>
                </div>
                {depth !== "basic" && s.top_cities?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-1 inline-flex items-center gap-1"><MapPin size={9} /> Top cities</p>
                    <ul className="flex flex-wrap gap-1.5">
                      {s.top_cities.slice(0, 8).map((c) => (
                        <li key={`${c.city}-${c.region}`} className="text-[10px] font-mono px-2 py-0.5 rounded border border-ink-600 text-zinc-300 bg-ink-900/60">
                          {c.city}{c.region ? ` · ${c.region}` : ""} <span className="text-neon-cyan ml-1">{c.plays}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {depth === "full" && s.postal_codes?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-amber-300 mb-1">Owner · ZIP codes</p>
                    <ul className="flex flex-wrap gap-1.5">
                      {s.postal_codes.slice(0, 12).map((p) => (
                        <li key={`${p.postal}-${p.city}`} className="text-[10px] font-mono px-2 py-0.5 rounded border border-amber-300/30 text-amber-200 bg-amber-500/5">
                          {p.postal}{p.city ? ` · ${p.city}` : ""} <span className="text-amber-300 ml-1">{p.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {s.video_url && (
                  <a href={s.video_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-neon-cyan">
                    <ArrowSquareOut size={10} /> Music video
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {data.submissions?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Submissions history</p>
          <ul className="space-y-1.5">
            {data.submissions.map((sub) => (
              <li key={sub.id} className="text-xs flex items-center justify-between gap-3 px-3 py-1.5 rounded border border-ink-700 bg-ink-800/40">
                <span className="text-zinc-300 truncate">{sub.song_title || "(untitled)"}</span>
                <span className="font-mono text-[10px] text-zinc-500">{(sub.created_at || "").slice(0, 10)}</span>
                <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${
                  sub.status === "promoted" ? "text-emerald-400 border-emerald-400/30 bg-emerald-500/10"
                  : sub.status === "rejected" ? "text-zinc-500 border-ink-600 bg-ink-800"
                  : "text-amber-300 border-amber-400/30 bg-amber-500/10"
                }`}>{sub.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
